import { ReactNode, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { clsx } from "clsx";
import { haptic } from "../lib/haptics";

const TRIGGER_DISTANCE = 80;
const MAX_PULL = 140;
const RESISTANCE = 0.5;

interface Props {
  onRefresh: () => Promise<void>;
  children: ReactNode;
}

export function PullToRefresh({ onRefresh, children }: Props) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const scrollEl = useRef<HTMLDivElement>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    if (refreshing) return;
    const el = scrollEl.current;
    if (!el) return;
    if (el.scrollTop > 0) return; // only when at top
    startY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null || refreshing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta <= 0) {
      setPull(0);
      return;
    }
    const pulled = Math.min(MAX_PULL, delta * RESISTANCE);
    setPull(pulled);
  };

  const onTouchEnd = async () => {
    if (refreshing) return;
    if (pull >= TRIGGER_DISTANCE) {
      haptic("medium");
      setRefreshing(true);
      try {
        await onRefresh();
      } catch {
        // ignore
      } finally {
        setRefreshing(false);
        setPull(0);
        startY.current = null;
      }
    } else {
      setPull(0);
      startY.current = null;
    }
  };

  const indicatorOpacity = Math.min(1, pull / TRIGGER_DISTANCE);
  const indicatorRotate = (pull / TRIGGER_DISTANCE) * 180;

  return (
    <div
      ref={scrollEl}
      className="relative min-h-0 flex-1 overflow-y-auto"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center"
        style={{ height: `${pull}px` }}
      >
        <div
          className="flex h-12 w-12 items-center justify-center"
          style={{ opacity: refreshing ? 1 : indicatorOpacity, marginTop: `${Math.max(0, pull - 48)}px` }}
        >
          <RefreshCw
            className={clsx("h-5 w-5 text-brand-500", refreshing && "animate-spin")}
            style={{
              transform: refreshing ? undefined : `rotate(${indicatorRotate}deg)`,
              transition: refreshing ? "none" : "transform 80ms linear",
            }}
          />
        </div>
      </div>
      <div
        style={{
          transform: `translateY(${pull}px)`,
          transition: startY.current === null && !refreshing ? "transform 200ms cubic-bezier(0.2,0.9,0.3,1)" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
